import React, { useState, useEffect, useRef } from 'react';
import { getOrganizationLeads, getUsersByOrganization, getRoleName, getCallsByOrganization, getStatusStatistics, getEmployeeStatusChanges } from '../../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

function CallcenterDashboard({ user }) {
  const [stats, setStats] = useState({
    totalLeads: 0,
    open: 0,
    notReached: 0,
    callback: 0,
    rejected: 0,
    qualifiedPV: 0,
    qualifiedWP: 0,
    notQualifiedPV: 0,
    notQualifiedWP: 0,
    statusPV: {
      'Offen': 0,
      'Qualifiziert': 0,
      'Kein Interesse mehr': 0,
      'Widerruf': 0,
      'Telefonnummer ungültig': 0,
      'Test Lead': 0
    },
    statusWP: {
      'Offen': 0,
      'Qualifiziert': 0,
      'Kein Interesse mehr': 0,
      'Widerruf': 0,
      'Telefonnummer ungültig': 0,
      'Test Lead': 0
    },
    statusCustomer: {
      'Erfasst': 0,
      'On Hold': 0,
      'Termin Storniert': 0
    },
    loading: true,
    error: null
  });

  const [chartData, setChartData] = useState({
    agentPerformance: [],
    dailyLeads: [],
    loading: true
  });

  const [callcenterUsers, setCallcenterUsers] = useState([]);
  const [callStats, setCallStats] = useState({});
  const [statusStats, setStatusStats] = useState({ loading: true, data: [] });
  const [employeeStatusChanges, setEmployeeStatusChanges] = useState({ loading: true, data: [] });

  const tableContainerRef = useRef(null);

  useEffect(() => {
    const loadCallcenterStats = async () => {
      if (!user?.isCallcenterLeader || !user?.organization_id) {
        setStats(prev => ({ ...prev, loading: false, error: 'Keine Berechtigung oder Organisations-ID!' }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }
      try {
        setStats(prev => ({ ...prev, loading: true, error: null }));
        setChartData(prev => ({ ...prev, loading: true }));
        setStatusStats(prev => ({ ...prev, loading: true }));
        setEmployeeStatusChanges(prev => ({ ...prev, loading: true }));
        
        const usersResponse = await getUsersByOrganization(user.id, user.organization_id);
        let callcenterUserList = [];
        if (usersResponse.success && Array.isArray(usersResponse.users)) {
          callcenterUserList = usersResponse.users.filter(u => u.role_type === 4 || u.role_type === 5);
        }
        setCallcenterUsers(callcenterUserList);
        
        const callsResponse = await getCallsByOrganization(user.id, user.organization_id);
        let calls = [];
        if (callsResponse.success && Array.isArray(callsResponse.calls)) {
          calls = callsResponse.calls;
        }
        
        const statusResponse = await getStatusStatistics(user.id, user.organization_id);
        if (statusResponse.success) {
          setStatusStats({ loading: false, data: statusResponse.user_stats || [] });
        } else {
          setStatusStats({ loading: false, data: [] });
        }

        // Neue API für Mitarbeiter-Status-Änderungen
        const employeeStatusResponse = await getEmployeeStatusChanges(user.id, user.organization_id);
        if (employeeStatusResponse.success) {
          setEmployeeStatusChanges({ loading: false, data: employeeStatusResponse.employee_stats || [] });
        } else {
          setEmployeeStatusChanges({ loading: false, data: [] });
        }
        
        const statsByUser = {};
        callcenterUserList.forEach(u => {
          statsByUser[u.id] = {
            name: u.full_name || u.email || 'Unbekannt',
            role: getRoleName(u.role_type),
            total: 0,
            notReached: 0,
            callback: 0,
            reached: 0,
            // Neue Felder für Status-Änderungen
            statusChangesPV: 0,
            statusChangesWP: 0,
            statusChangesCustomer: 0,
            qualifiedPV: 0,
            qualifiedWP: 0,
            notQualifiedPV: 0,
            notQualifiedWP: 0
          };
        });

        // Call-Statistiken verarbeiten
        calls.forEach(call => {
          if (statsByUser[call.user_id]) {
            statsByUser[call.user_id].total++;
            if (call.status === 'Nicht erreicht') statsByUser[call.user_id].notReached++;
            if (call.status === 'Rückruf erwünscht' || call.status === 'Später anrufen') statsByUser[call.user_id].callback++;
            if (
              call.status !== 'Nicht erreicht' &&
              call.status !== 'Rückruf erwünscht' &&
              call.status !== 'Später anrufen' &&
              call.status !== 'Abgelehnt'
            ) {
              statsByUser[call.user_id].reached++;
            }
          }
        });

        // Status-Änderungen verarbeiten
        if (employeeStatusResponse.success && Array.isArray(employeeStatusResponse.employee_stats)) {
          employeeStatusResponse.employee_stats.forEach(employee => {
            if (statsByUser[employee.user_id]) {
              // Status-Änderungen nach Produkttyp zählen
              if (employee.status_changes.PV) {
                statsByUser[employee.user_id].statusChangesPV = Object.values(employee.status_changes.PV).reduce((sum, count) => sum + count, 0);
                statsByUser[employee.user_id].qualifiedPV = employee.status_changes.PV['Qualifiziert'] || 0;
                statsByUser[employee.user_id].notQualifiedPV = (employee.status_changes.PV['Kein Interesse mehr'] || 0) + 
                                                              (employee.status_changes.PV['Widerruf'] || 0) + 
                                                              (employee.status_changes.PV['Telefonnummer ungültig'] || 0);
              }
              if (employee.status_changes.WP) {
                statsByUser[employee.user_id].statusChangesWP = Object.values(employee.status_changes.WP).reduce((sum, count) => sum + count, 0);
                statsByUser[employee.user_id].qualifiedWP = employee.status_changes.WP['Qualifiziert'] || 0;
                statsByUser[employee.user_id].notQualifiedWP = (employee.status_changes.WP['Kein Interesse mehr'] || 0) + 
                                                               (employee.status_changes.WP['Widerruf'] || 0) + 
                                                               (employee.status_changes.WP['Telefonnummer ungültig'] || 0);
              }
              if (employee.status_changes.CUSTOMER) {
                statsByUser[employee.user_id].statusChangesCustomer = Object.values(employee.status_changes.CUSTOMER).reduce((sum, count) => sum + count, 0);
              }
            }
          });
        }

        setCallStats(statsByUser);
        const response = await getOrganizationLeads(user.id, user.organization_id);
        if (response.success) {
          let leads = response.leads || [];
          leads = leads.filter(l => l.status_pv !== 'Test Lead' && l.status_wp !== 'Test Lead');
          
          const statusPV = { 'Offen': 0, 'Qualifiziert': 0, 'Kein Interesse mehr': 0, 'Widerruf': 0, 'Telefonnummer ungültig': 0, 'Test Lead': 0 };
          const statusWP = { 'Offen': 0, 'Qualifiziert': 0, 'Kein Interesse mehr': 0, 'Widerruf': 0, 'Telefonnummer ungültig': 0, 'Test Lead': 0 };
          const statusCustomer = { 'Erfasst': 0, 'On Hold': 0, 'Termin Storniert': 0 };
          
          leads.forEach(lead => {
            if (lead.status_pv) {
              if (statusPV.hasOwnProperty(lead.status_pv)) {
                statusPV[lead.status_pv]++;
              } else {
                statusPV['Offen']++;
              }
            } else if (lead.produkt && lead.produkt.toLowerCase().includes('pv')) {
              statusPV['Offen']++;
            }
            
            if (lead.status_wp) {
              if (statusWP.hasOwnProperty(lead.status_wp)) {
                statusWP[lead.status_wp]++;
              } else {
                statusWP['Offen']++;
              }
            } else if (lead.produkt && lead.produkt.toLowerCase().includes('wp')) {
              statusWP['Offen']++;
            }
            
            if (lead.customer_status) {
              if (statusCustomer.hasOwnProperty(lead.customer_status)) {
                statusCustomer[lead.customer_status]++;
              } else {
                statusCustomer['Erfasst']++;
              }
            } else {
              statusCustomer['Erfasst']++;
            }
          });
          
          const agentPerformance = callcenterUserList.map(u => {
            const perf = statsByUser[u.id] || { 
              name: u.full_name || u.email || 'Unbekannt', 
              total: 0, 
              notReached: 0, 
              callback: 0, 
              reached: 0,
              statusChangesPV: 0,
              statusChangesWP: 0,
              statusChangesCustomer: 0,
              qualifiedPV: 0,
              qualifiedWP: 0,
              notQualifiedPV: 0,
              notQualifiedWP: 0
            };
            return {
              ...perf
            };
          }).sort((a, b) => b.total - a.total);
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
          const dailyMap = {};
          for (let i = 13; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
            dailyMap[dateStr] = 0;
          }
          leads.forEach(lead => {
            const leadDate = new Date(lead.erstellt);
            if (leadDate >= twoWeeksAgo) {
              const dateStr = leadDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
              if (dailyMap.hasOwnProperty(dateStr)) {
                dailyMap[dateStr]++;
              }
            }
          });
          const dailyLeads = Object.entries(dailyMap).map(([date, count]) => ({ date, leads: count }));
          setStats({
            totalLeads: leads.length,
            open: leads.filter(l => (l.signature || l.opt_in) && (!l.has_calls || l.call_count === 0)).length,
            notReached: leads.filter(l => l.last_call_status === 'Nicht erreicht').length,
            callback: leads.filter(l => l.last_call_status === 'Rückruf erwünscht' || l.last_call_status === 'Später anrufen').length,
            rejected: leads.filter(l => l.last_call_status === 'Abgelehnt').length,
            qualifiedPV: leads.filter(l => l.status_pv === 'Qualifiziert').length,
            qualifiedWP: leads.filter(l => l.status_wp === 'Qualifiziert').length,
            notQualifiedPV: leads.filter(l => l.status_pv && l.status_pv !== 'Qualifiziert').length,
            notQualifiedWP: leads.filter(l => l.status_wp && l.status_wp !== 'Qualifiziert').length,
            statusPV: statusPV,
            statusWP: statusWP,
            statusCustomer: statusCustomer,
            loading: false,
            error: null
          });
          setChartData({ agentPerformance, dailyLeads, loading: false });
        } else {
          setStats(prev => ({ ...prev, loading: false, error: response.error || 'Fehler beim Laden der Daten' }));
          setChartData(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        setStats(prev => ({ ...prev, loading: false, error: 'Netzwerkfehler: ' + err.message }));
        setChartData(prev => ({ ...prev, loading: false }));
        setStatusStats(prev => ({ ...prev, loading: false }));
        setEmployeeStatusChanges(prev => ({ ...prev, loading: false }));
      }
    };
    loadCallcenterStats();
  }, [user]);

  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 200;
      tableContainerRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="team-dashboard-container">
      <div className="team-dashboard-header">
        <h2>Callcenter Dashboard - Statistiken</h2>
        <span className="team-name">Mein Callcenter</span>
      </div>
      {stats.loading ? (
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <span>Lade Callcenter-Statistiken...</span>
        </div>
      ) : stats.error ? (
        <div className="stats-error">
          <span>{stats.error}</span>
        </div>
      ) : (
        <>
          <div className="team-stats-grid">
            {/* Leads eingegangen */}
            <div className="stat-card total-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalLeads}</div>
                <div className="stat-label">Leads eingegangen</div>
              </div>
            </div>
            {/* Offen */}
            <div className="stat-card open-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.open}</div>
                <div className="stat-label">Offen</div>
                <div className="stat-percentage">{getPercentage(stats.open, stats.totalLeads)}%</div>
              </div>
            </div>
            {/* Nicht erreicht */}
            <div className="stat-card not-reached-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.notReached}</div>
                <div className="stat-label">Nicht erreicht</div>
                <div className="stat-percentage">{getPercentage(stats.notReached, stats.totalLeads)}%</div>
              </div>
            </div>
            {/* Rückruf/Später anrufen */}
            <div className="stat-card callback-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19l-7-7 7-7"></path>
                  <path d="M5 12h14"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.callback}</div>
                <div className="stat-label">Rückruf/Später anrufen</div>
                <div className="stat-percentage">{getPercentage(stats.callback, stats.totalLeads)}%</div>
              </div>
            </div>
            {/* Abgelehnt */}
            <div className="stat-card rejected-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.rejected}</div>
                <div className="stat-label">Abgelehnt</div>
                <div className="stat-percentage">{getPercentage(stats.rejected, stats.totalLeads)}%</div>
              </div>
            </div>
            {/* Qualifiziert (PV/WP) */}
            <div className="stat-card qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.qualifiedPV + stats.qualifiedWP}</div>
                <div className="stat-label">Qualifiziert (PV/WP)</div>
                <div className="stat-percentage">{getPercentage(stats.qualifiedPV + stats.qualifiedWP, stats.totalLeads)}%</div>
              </div>
            </div>
            {/* Nicht qualifiziert (PV/WP) */}
            <div className="stat-card not-qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.notQualifiedPV + stats.notQualifiedWP}</div>
                <div className="stat-label">Nicht qualifiziert (PV/WP)</div>
                <div className="stat-percentage">{getPercentage(stats.notQualifiedPV + stats.notQualifiedWP, stats.totalLeads)}%</div>
              </div>
            </div>
          </div>

          {/* Status-Übersicht */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginTop: '32px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' }}>Status-Übersicht</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {/* PV Status */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: '#4a5568' }}>PV Status</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {Object.entries(stats.statusPV).map(([status, count]) => (
                    <div key={status} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f7fafc', borderRadius: '6px' }}>
                      <span style={{ fontSize: '0.875rem', color: '#4a5568' }}>{status}</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* WP Status */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600', color: '#4a5568' }}>WP Status</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {Object.entries(stats.statusWP).map(([status, count]) => (
                    <div key={status} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f7fafc', borderRadius: '6px' }}>
                      <span style={{ fontSize: '0.875rem', color: '#4a5568' }}>{status}</span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
            </div>
          </div>

          {/* Status-Änderungen der Mitarbeiter */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginTop: '32px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' }}>Status-Änderungen der Mitarbeiter</h3>
            {statusStats.loading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#718096' }}>
                <div className="loading-spinner"></div>
                <span>Lade Status-Statistiken...</span>
              </div>
            ) : statusStats.data.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f7fafc' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Mitarbeiter</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Gesamt</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>PV</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>WP</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Customer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusStats.data.map((userStat, idx) => (
                      <tr key={userStat.user_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '12px 8px', fontWeight: 500 }}>{userStat.user_name}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#4a6de5' }}>{userStat.total_changes}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          {userStat.status_breakdown.PV ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {Object.entries(userStat.status_breakdown.PV).map(([status, count]) => (
                                <div key={status} style={{ fontSize: '0.75rem', color: '#4a5568' }}>
                                  {status}: {count}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          {userStat.status_breakdown.WP ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {Object.entries(userStat.status_breakdown.WP).map(([status, count]) => (
                                <div key={status} style={{ fontSize: '0.75rem', color: '#4a5568' }}>
                                  {status}: {count}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          {userStat.status_breakdown.CUSTOMER ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {Object.entries(userStat.status_breakdown.CUSTOMER).map(([status, count]) => (
                                <div key={status} style={{ fontSize: '0.75rem', color: '#4a5568' }}>
                                  {status}: {count}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#a0aec0' }}>
                <span>Keine Status-Änderungen verfügbar</span>
              </div>
            )}
          </div>

          {/* Agenten-Performance Tabelle mit Scroll-Buttons */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginTop: '32px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', position: 'relative' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' }}>Mitarbeiter-Performance</h3>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <div ref={tableContainerRef} style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ minWidth: '100%', width: 'max-content', borderCollapse: 'collapse', marginBottom: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f7fafc' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Rolle</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Anrufe gesamt</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Nicht erreicht</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Rückruf/Später</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Erreicht</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Status-Änderungen PV</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Status-Änderungen WP</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Qualifiziert PV</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap', width: 'auto' }}>Qualifiziert WP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(callStats).map((stat, idx) => (
                      <tr key={stat.name + idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', width: 'auto' }}>{stat.name}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', width: 'auto' }}>{stat.role}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', width: 'auto' }}>{stat.total}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', width: 'auto' }}>{stat.notReached}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', width: 'auto' }}>{stat.callback}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', width: 'auto' }}>{stat.reached}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#38a169', whiteSpace: 'nowrap', width: 'auto' }}>{stat.statusChangesPV}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3182ce', whiteSpace: 'nowrap', width: 'auto' }}>{stat.statusChangesWP}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#38a169', fontWeight: '600', whiteSpace: 'nowrap', width: 'auto' }}>{stat.qualifiedPV}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#3182ce', fontWeight: '600', whiteSpace: 'nowrap', width: 'auto' }}>{stat.qualifiedWP}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {chartData.loading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
                <div className="loading-spinner"></div>
                <span>Lade Chart-Daten...</span>
              </div>
            ) : chartData.agentPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.agentPerformance} margin={{ top: 20, right: 30, left: 20, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#4a5568' }} interval={0} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#4a5568' }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#4a6de5" name="Anrufe gesamt" />
                  <Bar dataKey="notReached" fill="#e53e3e" name="Nicht erreicht" />
                  <Bar dataKey="callback" fill="#ecc94b" name="Rückruf/Später" />
                  <Bar dataKey="reached" fill="#718096" name="Erreicht" />
                  <Bar dataKey="statusChangesPV" fill="#38a169" name="Status-Änderungen PV" />
                  <Bar dataKey="statusChangesWP" fill="#3182ce" name="Status-Änderungen WP" />
                  <Bar dataKey="qualifiedPV" fill="#68d391" name="Qualifiziert PV" />
                  <Bar dataKey="qualifiedWP" fill="#63b3ed" name="Qualifiziert WP" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a0aec0' }}>
                <span>Keine Mitarbeiter-Daten verfügbar</span>
              </div>
            )}
          </div>
          {/* Tagesverlauf Chart */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginTop: '32px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' }}>Leads pro Tag</h3>
            {chartData.loading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
                <div className="loading-spinner"></div>
                <span>Lade Chart-Daten...</span>
              </div>
            ) : chartData.dailyLeads.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.dailyLeads} margin={{ top: 20, right: 30, left: 20, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#4a5568' }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#4a5568' }} />
                  <Tooltip />
                  <Bar dataKey="leads" fill="#4a6de5" name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a0aec0' }}>
                <span>Keine Tagesdaten verfügbar</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CallcenterDashboard; 